import { kvSet as apiKvSet, kvSetBatch, kvGetAll } from "@/api/kv";
import { setLocalStorage, getLocalStorage, getCacheV1 } from "@/utils/common";

/**
 * 需要同步到 KV API 的 key 列表（固定 key）
 * 这些 key 在写入 localStorage 的同时会 fire-and-forget 同步到云端
 * 登录时会从云端拉取最新数据覆盖本地
 */
const SYNC_KEYS = [
  "await_claim",
  "task_complete_nums",
  "task_complete_num",
  "post_message_num",
  "share_group_num",
  "completed_steps",
  "reported_steps",
  "showFinger",
];

/**
 * 需要同步的动态 key 前缀列表
 */
const SYNC_KEY_PREFIXES = ["post_id_"];

/**
 * 判断 key 是否需要同步（支持动态 key 如 post_id_xxx）
 */
function isSyncKey(key) {
  return SYNC_KEYS.includes(key) || SYNC_KEY_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * 从 localStorage 中扫描所有匹配动态前缀的 key（去掉 project_id 前缀后的业务 key）
 */
function getDynamicSyncKeys() {
  const prefix = TApp_GlobalConfig.project_id + "_";
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const rawKey = localStorage.key(i);
    if (!rawKey.startsWith(prefix)) continue;
    const bizKey = rawKey.slice(prefix.length);
    if (SYNC_KEY_PREFIXES.some((p) => bizKey.startsWith(p))) {
      keys.push(bizKey);
    }
  }
  return keys;
}

/**
 * 是否已登录
 */
function isLoggedIn() {
  return Boolean(getCacheV1("token"));
}

/**
 * 写入数据：localStorage + KV API 同步
 * @param {string} key
 * @param {any} value
 */
export function kvCacheSet(key, value) {
  // 始终写入 localStorage（保证本地即时可用）
  setLocalStorage(key, value);

  // 已登录时，fire-and-forget 同步到云端（跳过无意义的值）
  if (isLoggedIn() && isSyncKey(key) && value !== 0 && value !== "0" && value !== "" && value !== null) {
    apiKvSet(key, value).catch(() => {});
  }
}

/**
 * 读取数据：直接从 localStorage 读取（同步，快速）
 * @param {string} key
 * @returns {string|null}
 */
export function kvCacheGet(key) {
  return getLocalStorage(key);
}

/**
 * 登录成功后从云端同步数据到本地
 * 使用 GET /api/kv/all 一次拉取全部数据，云端数据优先覆盖本地
 */
export async function kvSyncFromCloud() {
  if (!isLoggedIn()) return;

  try {
    const res = await kvGetAll();
    if (res.code !== 200 || !res.data) return;

    // 遍历 account 层数据写入 localStorage
    const accountData = res.data.account || {};
    for (const [key, value] of Object.entries(accountData)) {
      // 跳过值为 0、"0"、空字符串、null 的数据
      if (value === 0 || value === "0" || value === "" || value === null) continue;
      const storageValue = typeof value === "object" ? JSON.stringify(value) : String(value);
      setLocalStorage(key, storageValue);
    }
  } catch (error) {
    console.error("[kvCache] syncFromCloud failed:", error);
  }
}

/**
 * 将本地所有需要同步的 key 批量上传到云端
 * 使用 PUT /api/kv/batch 一次请求上传，最多 50 个
 */
export async function kvSyncToCloud() {
  if (!isLoggedIn()) return;

  try {
    const items = {};

    // 收集固定 key（跳过无意义的值）
    for (const key of SYNC_KEYS) {
      const value = getLocalStorage(key);
      if (value !== null && value !== undefined && value !== "" && value !== "0" && value !== 0) {
        items[key] = value;
      }
    }

    // 收集动态 key（如 post_id_xxx）
    for (const key of getDynamicSyncKeys()) {
      const value = getLocalStorage(key);
      if (value !== null && value !== undefined && value !== "" && value !== "0" && value !== 0) {
        items[key] = value;
      }
    }

    if (Object.keys(items).length === 0) return;

    // 批量上传，每次最多 50 个
    const entries = Object.entries(items);
    for (let i = 0; i < entries.length; i += 50) {
      const batch = Object.fromEntries(entries.slice(i, i + 50));
      await kvSetBatch(batch, { tier: "account" });
    }
  } catch (error) {
    console.error("[kvCache] syncToCloud failed:", error);
  }
}
